import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { forkJoin, Observable, of, throwError } from "rxjs";
import { catchError, switchMap, tap, map } from "rxjs/operators";
import { Router } from "@angular/router";
import { jwtDecode } from "jwt-decode";
import { ApiUrlService } from "./api-url.service";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import { CacheService } from "./cache.service";
import { ReactNativeBundleComponent } from "../react-native-bundle/react-native-bundle.component";
import { FeatureService } from "./features.service";

interface Role {
  role_id: number;
  role_name: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private tokenUrl = "/token";
  private userUrl = "/api/users/me";
  private rolesUrl = "/api/roles";
  private orgUrl = "/api/organizations";
  private tokenKey = "authToken";
  private userInfoKey = "userInfo";
  private rolesKey = "userRoles";
  private orgKey = "userOrganization";

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiUrlService: ApiUrlService,
    private cacheService: CacheService,
    private featureService: FeatureService
  ) {}

  // top
  private async getFilesystemItemString(key: string): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      // 1) Try Filesystem
      try {
        const { data } = await Filesystem.readFile({
          path: key,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const s = (data as string) ?? null;
        if (s !== null && s !== "null" && s !== "") return s;
      } catch (_) {
        /* swallow and fall back */
      }

      // 2) Fallback to Preferences only (removed localStorage fallback for native)
      try {
        const { value } = await Preferences.get({ key });
        if (value && value !== "null" && value !== "") return value;
      } catch (_) {}

      return null;
    } else {
      try {
        const v = localStorage.getItem(key);
        return v && v !== "null" && v !== "" ? v : null;
      } catch {
        return null;
      }
    }
  }

  private async getFilesystemItemJson<T = any>(key: string): Promise<T | null> {
    const raw = await this.getFilesystemItemString(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  // Helper function to set data in Filesystem (or localStorage for web)
  private async setFilesystemItem(key: string, value: any): Promise<void> {
    // Only stringify if the value is not already a string
    const dataToStore =
      typeof value === "string" ? value : JSON.stringify(value);

    if (Capacitor.isNativePlatform()) {
      // 1) Write to filesystem
      await Filesystem.writeFile({
        path: key,
        data: dataToStore,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      // 2) Write to Preferences
      try {
        console.log(`Saving ${key} to Preferences`);
        await Preferences.set({ key, value: dataToStore });
        console.log(`✅ Saved ${key} to Preferences`);
      } catch (err) {
        console.error(`⛔️ Preferences.set failed for key "${key}"`, err);
      }

      // 3) Legacy: write to localStorage last, but don’t let it break anything else
      try {
        localStorage.setItem(key, dataToStore);
      } catch (err) {
        console.warn(`⚠️ localStorage.setItem failed for key "${key}"`, err);
      }
    } else {
      // In a browser, still wrap localStorage in try/catch
      try {
        localStorage.setItem(key, dataToStore);
      } catch (err) {
        console.warn(`⚠️ localStorage.setItem failed for key "${key}"`, err);
      }
    }
  }

  // Helper function to remove data from Filesystem (or localStorage for web)
  private async removeFilesystemItem(key: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      localStorage.removeItem(key); // TODO keep removing here as well for legacy features, will slowly remove

      // setFilesystemItem() writes this same key to both Filesystem AND
      // Preferences - clearing only the Filesystem copy left Preferences
      // holding the stale pre-clear value. getFilesystemItemString() falls
      // back to Preferences when the Filesystem read fails (e.g. right
      // after this delete), so a caller like clearOrganization()/
      // clearUserInfo() (used by org/linked-account switching) looked like
      // it had done nothing - worse, isLoggedIn()'s native corruption
      // check reads Filesystem and Preferences independently and treats
      // "present in one but not the other" as a genuine mismatch, so this
      // incomplete clear was itself manufacturing the exact signal that
      // wipes the whole session and forces a re-login, only on native.
      try {
        await Preferences.remove({ key });
      } catch (err) {
        console.warn(`⚠️ Preferences.remove failed for key "${key}"`, err);
      }

      try {
        await Filesystem.deleteFile({
          path: key,
          directory: Directory.Data,
        });
      } catch (err) {
        // Expected if the file was never written (e.g. Preferences-only
        // data, or already cleared) - not itself a sign of corruption.
        console.warn(`⚠️ Filesystem.deleteFile failed for key "${key}"`, err);
      }
    } else {
      localStorage.removeItem(key);
    }
  }

  async login(username: string, password: string): Promise<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      accept: "application/json",
    });

    const body = new HttpParams()
      .set("username", username)
      .set("password", password);

    const response = await this.http
      .post<any>(
        this.apiUrlService.getApiUrl() + this.tokenUrl,
        body.toString(),
        { headers }
      )
      .toPromise();

    if (response && response.access_token) {
      console.log("Storing token:", response.access_token);
      await this.storeToken(response.access_token);
    }

    console.log("Getting user info, token was just stored");
    // getOrganization() and getRoles() both trust their own cache
    // unconditionally, with nothing that re-validates it against the
    // userInfo this login just fetched fresh - so if a stale org/roles
    // cache survives from an earlier session for any reason (e.g. a
    // previous logout's cleanup silently failing partway, which is caught
    // and swallowed rather than thrown), a brand new login could still
    // show the old org/roles. A login is always meant to start clean, so
    // clear both explicitly first rather than trusting whatever's left
    // over from before.
    await Promise.all([this.clearOrganization(), this.clearRoles()]);

    // Force fetch fresh userInfo from server and store in both Data and Preferences
    const userInfo = await this.fetchAndStoreUserInfo();
    await this.getRoles(); // Fetch roles and store them
    const userOrg = await this.getOrganization();

    return { userInfo, userOrg };
  }

  /**
   * Switch to a linked account (a different login/email, already
   * verified-linked to this one) by minting it a fresh token server-side -
   * no re-entering that account's password, since the linkage itself
   * already proved ownership of both sides. Mirrors the org-switch
   * cache-clearing so a subsequent reload picks up the new account's info
   * from scratch, same as switching active org within one account.
   */
  async switchToLinkedAccount(targetUserId: string): Promise<boolean> {
    try {
      const userInfo = await this.getUserInfo();
      const response = await this.http
        .post<any>(
          `${this.apiUrlService.getApiUrl()}/api/users/${
            userInfo.user_id
          }/linked-accounts/${targetUserId}/switch-token`,
          {}
        )
        .toPromise();
      if (!response?.access_token) return false;
      await this.storeToken(response.access_token);
      await Promise.all([
        this.clearOrganization(),
        this.clearUserInfo(),
        this.featureService.clearPersistedFeaturesCache(),
      ]);
      return true;
    } catch (error) {
      console.error("Failed to switch to linked account:", error);
      return false;
    }
  }

  private storeToken(token: string): Promise<void> {
    console.log("Storing Token Original", token);
    return this.setFilesystemItem(this.tokenKey, token);
  }

  // An org switch only updates active_organization_id; the legacy
  // organization_id stays the user's home org forever. Feature bundles read
  // organization_id straight from the cached userInfo, so rewrite it to the
  // active org here (the one place userInfo is cached) and keep the original
  // as home_organization_id. Idempotent - re-normalizing an already
  // normalized object keeps the original home org.
  private normalizeUserInfo(userInfo: any): any {
    if (!userInfo || !userInfo.active_organization_id) return userInfo;
    return {
      ...userInfo,
      home_organization_id:
        userInfo.home_organization_id ?? userInfo.organization_id,
      organization_id: userInfo.active_organization_id,
    };
  }

  private storeUserInfo(userInfo: any): Promise<void> {
    return this.setFilesystemItem(
      this.userInfoKey,
      this.normalizeUserInfo(userInfo)
    );
  }

  private storeRoles(roles: Role[]): Promise<void> {
    return this.setFilesystemItem(this.rolesKey, roles);
  }

  public async clearUserInfo(): Promise<void> {
    console.log("Clearing user info");
    await this.removeFilesystemItem(this.userInfoKey);
  }

  public async clearRoles(): Promise<void> {
    await this.removeFilesystemItem(this.rolesKey);
  }

  public async clearOrganization(): Promise<void> {
    await this.removeFilesystemItem(this.orgKey);
  }

  async hasRolesByName(requiredRoles: string[]): Promise<boolean> {
    const roles = await this.getRoles(); // Await async result of getRoles
    const hasRequiredRoles = requiredRoles.every((requiredRole) =>
      roles.some((role) => role.role_name === requiredRole)
    );
    return hasRequiredRoles;
  }

  async hasRoles(requiredRoles: Role[]): Promise<boolean> {
    const roles = await this.getRoles(); // Await async result of getRoles
    const hasRequiredRoles = requiredRoles.every((role) =>
      roles.includes(role)
    );
    return hasRequiredRoles;
  }

  async getToken(): Promise<string | null> {
    console.log("[AUTH] getToken() called");
    try {
      const token = await this.getFilesystemItemString(this.tokenKey); // Await the async operation to get the token
      console.log(
        "[AUTH] Token retrieved:",
        token ? "EXISTS (length: " + token.length + ")" : "NULL"
      );
      if (this.isTokenExpired(token)) {
        // Token is expired or invalid - clear all auth state to prevent inconsistent state
        console.warn("[AUTH] Token expired or invalid, clearing auth state");
        await this.clearAuthState();
        return null; // Return null to indicate no valid token
      } else {
        console.log("[AUTH] Token is valid");
        return token; // Return the token if it's valid
      }
    } catch (error) {
      console.error("[AUTH] Error retrieving token:", error);
      await this.clearAuthState(); // Clear auth state on error
      return null; // Return null if there's an error
    }
  }

  // Helper to clear auth state without navigation (used internally)
  private async clearAuthState(): Promise<void> {
    try {
      await Promise.all([
        this.removeFilesystemItem(this.tokenKey),
        this.removeFilesystemItem(this.userInfoKey),
        this.removeFilesystemItem(this.rolesKey),
        this.removeFilesystemItem(this.orgKey),
      ]);
    } catch (error) {
      console.error("Error clearing auth state:", error);
    }
  }

  /**
   * Native-only: detect a genuinely corrupted migration state, where
   * userInfo exists in exactly ONE of the two storage locations (Data
   * directory vs Preferences) but not the other. That mismatch can't
   * happen through normal use and is the real signal something's broken.
   *
   * Deliberately does NOT flag "missing from both" as corruption - that's
   * also the normal, expected state right after clearUserInfo() (e.g. the
   * org-switch flow clears it on purpose, then reloads, relying on
   * getUserInfo() to re-fetch fresh data with the still-valid token).
   * Treating that as corruption used to wipe the token itself and force a
   * real re-login on every org switch.
   */
  private async userInfoStorageMismatched(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    let dataExists = false;
    let preferencesExists = false;

    try {
      const { data } = await Filesystem.readFile({
        path: this.userInfoKey,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      dataExists = !!(data && data !== "null" && data !== "");
      console.log(
        "[AUTH] userInfo in Data directory:",
        dataExists ? "EXISTS" : "MISSING"
      );
    } catch (e) {
      console.log(
        "[AUTH] userInfo in Data directory: MISSING (file not found)"
      );
      dataExists = false;
    }

    try {
      const { value } = await Preferences.get({ key: this.userInfoKey });
      preferencesExists = !!(value && value !== "null" && value !== "");
      console.log(
        "[AUTH] userInfo in Preferences:",
        preferencesExists ? "EXISTS" : "MISSING"
      );
    } catch (e) {
      console.log("[AUTH] userInfo in Preferences: MISSING");
      preferencesExists = false;
    }

    const mismatched = dataExists !== preferencesExists;
    console.log(
      "[AUTH] userInfo storage validation:",
      mismatched ? "MISMATCHED (possible corruption)" : "CONSISTENT"
    );
    return mismatched;
  }

  async isLoggedIn(): Promise<boolean> {
    console.log("[AUTH] ========== isLoggedIn() called ==========");
    const token = await this.getToken(); // Await the async result of getToken
    if (!token) {
      console.log("[AUTH] isLoggedIn() returning FALSE - no token");
      return false;
    }

    // A valid token is sufficient proof of a logged-in session - userInfo
    // is just a cache of it and its absence alone is not a corruption
    // signal (see userInfoStorageMismatched() above). Only check for an
    // actual native storage mismatch, which IS a genuine red flag.
    const mismatched = await this.userInfoStorageMismatched();
    if (mismatched) {
      console.warn(
        "[AUTH] userInfo exists in only one native storage location - clearing auth state"
      );
      await this.clearAuthState();
      console.log(
        "[AUTH] isLoggedIn() returning FALSE - userInfo storage mismatch"
      );
      return false;
    }

    console.log("[AUTH] isLoggedIn() returning TRUE - token is valid");
    return true;
  }

  async logout(): Promise<void> {
    this.sendLogoutMessage();

    // Clear all cached React Native features and containers
    ReactNativeBundleComponent.clearAllCachedFeatures();

    // Clear in-memory feature descriptions cache
    this.featureService.clearFeatureDescriptionsCache();

    // Give other frames / your React Native webview a moment to handle it
    await new Promise((res) => setTimeout(res, 20));

    // Clear all stored data across web and native BEFORE navigating
    await this.clearAllAppStorage();

    // Navigate to login after clearing storage to prevent flash of home screen
    this.router.navigate(["/login"]);
  }

  // Wipe everything we control: CacheService, Capacitor Preferences, Filesystem Data files,
  // browser storages (localStorage/sessionStorage) and Cache Storage (if available).
  private async clearAllAppStorage(): Promise<void> {
    try {
      // Clear CacheService-managed resources and cached data (features, orders, etc.)
      // This clears everything except biometric credentials and auth tokens (which are cleared separately below)
      if (
        this.cacheService &&
        typeof this.cacheService.clearAllCachedData === "function"
      ) {
        await this.cacheService.clearAllCachedData();
      }

      // Also clear resource cache (images, bundles, etc.)
      if (
        this.cacheService &&
        typeof this.cacheService.clearAllResources === "function"
      ) {
        await this.cacheService.clearAllResources();
      }

      // Clear Capacitor Preferences (native) but preserve biometric credentials
      try {
        // Save biometric credentials before clearing
        const biometricUsername = await Preferences.get({
          key: "biometric_username",
        });
        const biometricPassword = await Preferences.get({
          key: "biometric_password",
        });
        const biometricHasPassword = await Preferences.get({
          key: "biometric_has_password",
        });

        // Clear all preferences
        await Preferences.clear();

        // Restore biometric credentials if they existed
        if (biometricUsername.value) {
          await Preferences.set({
            key: "biometric_username",
            value: biometricUsername.value,
          });
        }
        if (biometricPassword.value) {
          await Preferences.set({
            key: "biometric_password",
            value: biometricPassword.value,
          });
        }
        if (biometricHasPassword.value) {
          await Preferences.set({
            key: "biometric_has_password",
            value: biometricHasPassword.value,
          });
        }
      } catch (e) {
        console.warn("Preferences.clear failed", e);
      }

      // Clear Filesystem Data directory entries created by this service (native)
      if (Capacitor.isNativePlatform()) {
        try {
          // Attempt to delete known keys first
          await Promise.all([
            this.removeFilesystemItem(this.tokenKey),
            this.removeFilesystemItem(this.userInfoKey),
            this.removeFilesystemItem(this.rolesKey),
            this.removeFilesystemItem(this.orgKey),
          ]);
        } catch (e) {
          console.warn("Failed removing known data files", e);
        }

        // Best-effort: iterate Directory.Data and delete any files we can see under root
        try {
          const list = await Filesystem.readdir({
            path: "",
            directory: Directory.Data,
          });
          if ((list as any)?.files?.length) {
            for (const f of (list as any).files) {
              try {
                await Filesystem.deleteFile({
                  path: f.name || f,
                  directory: Directory.Data,
                });
              } catch (e) {
                // ignore per-file errors
              }
            }
          }
        } catch (e) {
          // Some platforms may not allow listing Data root; ignore
        }
      }

      // Clear browser storages
      try {
        localStorage.clear();
      } catch {}
      try {
        sessionStorage.clear();
      } catch {}

      // Clear Cache Storage (PWA/browser) if available
      try {
        const globalAny: any =
          typeof self !== "undefined"
            ? self
            : typeof window !== "undefined"
            ? (window as any)
            : undefined;
        if (
          globalAny &&
          globalAny.caches &&
          typeof globalAny.caches.keys === "function"
        ) {
          const names: string[] = await globalAny.caches.keys();
          await Promise.all(
            names.map((n: string) => globalAny.caches.delete(n))
          );
        }
      } catch (e) {
        console.warn("Clearing Cache Storage failed", e);
      }
    } catch (e) {
      console.error("Failed to clear app storage", e);
    }
  }

  public sendLogoutMessage(): void {
    console.log("self:", self as any);
    console.log("self.clients:", (self as any).clients);

    if (typeof window !== "undefined") {
      console.log("Sending logout message to window:", window);
      window.postMessage({ type: "logout" }, "*");
    }

    if (typeof self !== "undefined") {
      console.log("Sending logout message to self:", self);
      (self as any).postMessage({ type: "logout" }, "*");
    }

    if (typeof self !== "undefined" && (self as any).clients) {
      (self as any).clients.matchAll().then((clients: any[]) => {
        clients.forEach((client: any) => {
          console.log("Sending logout message to client:", client);
          client.postMessage({ type: "logout" });
        });
      });
    } else {
      console.warn("self.clients is not available.");
    }
  }

  async resetPassword(userEmail: string): Promise<any> {
    const resetPasswordUrl = `/api/users/${userEmail}/forgot-password`;
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      accept: "application/json",
    });

    return this.http
      .post<any>(this.apiUrlService.getApiUrl() + resetPasswordUrl, null, {
        headers,
      })
      .toPromise();
  }

  /**
   * Fetch fresh userInfo from server and store in both Data directory and Preferences.
   * Used during login to ensure data is in both storage locations.
   */
  private async fetchAndStoreUserInfo(): Promise<any> {
    console.log("Fetching fresh userInfo from server");
    const token = await this.getToken();
    if (!token) {
      throw new Error("No token found");
    }

    const url = this.apiUrlService.getApiUrl() + this.userUrl;
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return new Promise((resolve, reject) => {
      this.http.get<any>(url, { headers }).subscribe({
        next: async (response) => {
          console.log("Fetched userInfo from server:", response);
          const userInfo = this.normalizeUserInfo(response);
          // Store in both Data directory and Preferences
          await this.storeUserInfo(userInfo);
          console.log("Stored userInfo in both Data directory and Preferences");
          resolve(userInfo);
        },
        error: (err) => {
          console.error("Error fetching user info:", err);
          reject(err);
        },
      });
    });
  }

  async getUserInfo(): Promise<any> {
    console.log("Inside getUserInfo");

    // CRITICAL: Always check for valid token first, even if we have cached user info
    // This prevents returning stale user info when token is expired/missing
    const token = await this.getToken();
    if (!token) {
      // Token is invalid/missing - clear any cached user info and redirect
      console.warn("No valid token in getUserInfo, clearing cached data");
      await this.clearAuthState();
      this.router.navigate(["/login"]);
      throw new Error("No token found");
    }

    const storedUserInfo = await this.getFilesystemItemJson(this.userInfoKey);
    if (storedUserInfo) {
      console.log("Returning stored user info");
      // Migrate a cache written before normalizeUserInfo existed, so
      // features reading it directly see the active org without waiting
      // for the next login / org switch.
      const normalized = this.normalizeUserInfo(storedUserInfo);
      if (normalized.organization_id !== storedUserInfo.organization_id) {
        await this.storeUserInfo(normalized);
      }
      return normalized;
    }

    console.log("No stored user info, fetching from server");

    const url = this.apiUrlService.getApiUrl() + this.userUrl;
    console.log("Token:", token);
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    console.log("Fetching user info from server", url);

    console.log("Bearer token being sent:", token);
    console.log("Authorization header:", headers.get("Authorization"));
    headers
      .keys()
      .forEach((key) => console.log(`Header ${key}:`, headers.get(key)));

    return new Promise((resolve, reject) => {
      this.http.get<any>(url, { headers }).subscribe({
        next: (response) => {
          console.log("Raw response data:", response);

          // If your API ever wraps data in .source, unwrap here
          let userInfo = this.normalizeUserInfo(response);

          console.log("Final userInfo object:", userInfo);
          this.storeUserInfo(userInfo)
            .then(() => resolve(userInfo))
            .catch((e) => {
              console.error("Error storing user info:", e);
              resolve(userInfo); // still resolve with fetched data
            });
        },
        error: (err) => {
          console.error("Error fetching user info:", err);
          if (err.status === 404) {
            this.logout();
          }
          reject(err);
        },
      });
    });
  }

  async getRoles(): Promise<Role[]> {
    // First, attempt to fetch the roles from the filesystem
    const storedRoles = await this.getFilesystemItemJson(this.rolesKey);
    if (storedRoles) {
      console.log("Returning roles from Filesystem");
      return storedRoles; // Return cached roles from Filesystem
    }

    // If no roles in cache, fetch the token
    const token = await this.getToken(); // Await async result of getToken
    if (!token) {
      this.router.navigate(["/login"]); // Redirect to login if no token
      throw new Error("No token found"); // Throw error if no token
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    console.log("Headers:", headers.get("Authorization"));

    try {
      // Await the result of getUserInfo() instead of using observable
      const userInfo = await this.getUserInfo(); // Await the async result of getUserInfo
      console.log("UserInfo received:", userInfo);

      // Use the roles for the user's ACTIVE org (orgs[]), not the legacy
      // top-level roles field - that field is only set at account
      // creation / for unmigrated single-org users, and is never updated
      // by an org switch or by roles assigned within a specific org. This
      // cache (userRoles) is also what org-admin's SuperAdminUtils and
      // several of its pages read directly from localStorage, so a stale
      // role here silently breaks admin permission checks there too.
      const activeOrgId =
        userInfo.active_organization_id || userInfo.organization_id;
      const orgs = userInfo.orgs || [];
      const activeOrgRoles = orgs.find(
        (org: any) => org.organization_id === activeOrgId
      )?.roles;
      const userRoles =
        activeOrgRoles && activeOrgRoles.length > 0
          ? activeOrgRoles
          : userInfo.roles
          ? userInfo.roles
          : []; // Get role_ids from userInfo

      // Fetch all roles (with role_id and role_name) from the server
      const rolesData = await this.http
        .get<Role[]>(`${this.apiUrlService.getApiUrl()}${this.rolesUrl}`, {
          headers,
        })
        .toPromise(); // Await the HTTP request

      console.log("Roles data received:", rolesData);

      // Map user role_ids to their corresponding { role_id, role_name }
      const userRoleDetails: Role[] = userRoles.map((roleId) => {
        const matchingRole = rolesData.find((role) => role.role_id === roleId);
        return matchingRole
          ? matchingRole
          : { role_id: roleId, role_name: "Unknown" };
      });

      // Optionally store the role details in Filesystem
      this.storeRoles(userRoleDetails);

      // Return the array of role objects with both role_id and role_name
      return userRoleDetails;
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw new Error("Failed to fetch roles");
    }
  }

  decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      return null;
    }
  }

  getTokenExpirationDate(token: string): Date | null {
    const decoded: any = this.decodeToken(token);

    if (decoded === null || !decoded.hasOwnProperty("exp")) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  isTokenExpired(token: string): boolean {
    const expirationDate = this.getTokenExpirationDate(token);
    return expirationDate === null
      ? true
      : !(expirationDate.valueOf() > new Date().valueOf());
  }

  async getOrganization(): Promise<any> {
    // First, attempt to fetch the organization from the filesystem
    const storedOrganization = await this.getFilesystemItemJson(this.orgKey);
    if (storedOrganization) {
      console.log("Returning organization from Filesystem");
      return storedOrganization; // Return cached organization from Filesystem
    }

    // If no organization in cache, fetch the token
    const token = await this.getToken(); // Await async result of getToken
    if (!token) {
      this.router.navigate(["/login"]); // Redirect to login if no token
      throw new Error("No token found"); // Instead of returning throwError, throw the error
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    console.log("Headers:", headers.get("Authorization"));

    try {
      // Await the result of getUserInfo() instead of using observable
      const userInfo = await this.getUserInfo(); // Await the async result of getUserInfo
      console.log("UserInfo received:", userInfo);

      // Prefer active_organization_id (the multi-org "which org am I
      // currently in" field) over the legacy organization_id, which an org
      // switch never touches on purpose - falling back to it only covers
      // users who haven't been migrated to the multi-org model at all.
      const organizationId =
        userInfo.active_organization_id || userInfo.organization_id;

      // Check if the organization ID is present
      if (!organizationId) {
        console.error("No organization ID found in user info");
        throw new Error("No organization ID found");
      }

      // Fetch the organization details using the organization_id
      const organizationData = await this.http
        .get<any>(
          `${this.apiUrlService.getApiUrl()}${this.orgUrl}/${organizationId}`,
          { headers }
        )
        .toPromise(); // Await the HTTP request

      console.log("Organization data received:", organizationData);

      // Optionally store the organization details in Filesystem
      this.setFilesystemItem(this.orgKey, organizationData);

      // Return the organization details
      return organizationData;
    } catch (error) {
      console.error("Error fetching organization:", error);
      throw new Error("Failed to fetch organization");
    }
  }
}

// curl -X 'GET' \
//   'https://api.medicnowsolutions.com/api/users/me' \
//   -H 'accept: application/json' \
//   -H 'Authorization: Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0QHRlc3QuY29tIiwiZXhwIjoxNzQ4OTc0NzUwfQ.Vm6EHYw6LJdh0zM4gc6SN_bSXlSe9hT_WoVokqLl7XKS_NyVoDyH12zcMzmgALvUvEZnq6-BnZ0t9rwbGYVEETEsenmonhuT0QA0bGndSf0JtATd6QRgI3gpqs8puFYpFU2CbEDNU1TxixZ8T3WYRXmeLNJWCo5oxjH2ZBPzOgo81RRJQ8zQwgOaY7FmyUC_B8P9MKdVEseiRff_Y366G2EQBhSAtzMYxj1D66CJme8UKG417joiRFCuBNzCqrDnzVXbgriZolvQjY4rCJj6AySm4VqHROtS-SDmaYSRzyA4Rl4wOBy7Y3r8Bw32Vb4yjY174KrRz4O6v7cy1Ej6pg'
